import { NgStyle } from '@angular/common';
import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { CustomerStateFacade, PositionsFacade } from '@zero/shared/util-global-state';
import { OrderData, OrderStatus, PushMessage, PushMessageType } from '@zero/shared/util-models';
import { AuthService, DataService } from '@zero/shared/util-web-app-services';
import { commonRoutes } from 'constants/web-app-constants';
import { debounceTime, filter } from 'rxjs';
import { SnackbarMsgComponent } from './snackbar-msg/snackbar-msg.component';
import { SnackbarOrderMsgComponent } from './snackbar-order-msg/snackbar-order-msg.component';
import { SnackbarTicketMsgComponent } from './snackbar-ticket/snackbar-ticket-msg.component';

@Component({
  selector: 'web-components-snackbar',
  templateUrl: './snackbar.component.html',
  imports: [NgStyle, SnackbarOrderMsgComponent, SnackbarMsgComponent, SnackbarTicketMsgComponent, RouterLink, TranslocoPipe],
})
export class SnackbarComponent implements OnInit {
  readonly #positionsFacade = inject(PositionsFacade);
  readonly #dataService = inject(DataService);
  readonly #authService = inject(AuthService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #customerStateFacade = inject(CustomerStateFacade);

  public messages: PushMessage[] = [];
  public readonly messageTypes = PushMessageType;
  public readonly memberHome = commonRoutes.postlogin.member_home;

  @ViewChild('snackbarContent') public snackbarContentEl: ElementRef;

  #snackBarShow = false;
  readonly #orderStatesToShow: OrderStatus[] = [
    OrderStatus.Canceled,
    OrderStatus.Filled,
    OrderStatus.Expired,
    OrderStatus.PartiallyFilled_Canceled,
    OrderStatus.PartiallyFilled_Expired,
    OrderStatus.Rejected,
    OrderStatus.PartiallyFilled_Filled,
    OrderStatus.PartiallyFilled,
  ];

  ngOnInit(): void {
    this.#dataService.msgSubject.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((msg) => {
      if (msg === null) {
        // customer changed
        this.#clearmsg();
      } else if (
        msg.type === PushMessageType.msg ||
        (msg.type === PushMessageType.order && this.#orderStatesToShow.indexOf((msg.msgData as OrderData).status) > -1) ||
        (msg.type === PushMessageType.ticket && msg.msgData.showInSnackbar === true)
      ) {
        this.messages.unshift(msg);
        this.messages = this.messages.slice(0, 3);
        this.#snackBarShow = true;
      }

      if (msg?.type === PushMessageType.ticket) {
        setTimeout(() => this.#dataService.triggerReloadUnreadTickets(), 2000);
      }
    });

    this.#authService
      .isCustomer$()
      .pipe(
        filter((isCustomer) => !!isCustomer),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#clearmsg());

    this.#dataService.msgSubject
      .pipe(
        filter((msg) => msg?.type === PushMessageType.order),
        debounceTime(1000),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.#positionsFacade.loadPositions(true);
        this.#customerStateFacade.loadCustomerAccountData(true);
      });
  }

  #clearmsg(): void {
    this.messages = [];
    this.#snackBarShow = false;
  }

  public snackbarStyle(): unknown {
    if (!this.snackbarContentEl?.nativeElement || typeof this.snackbarContentEl.nativeElement.clientHeight === 'undefined') {
      return { display: 'none' };
    }

    if (this.#snackBarShow === true) {
      return { bottom: '0px' };
    }

    return {
      bottom: `${this.snackbarContentEl.nativeElement.clientHeight * -1 - 2}px`,
    };
  }

  public clearAndClose(): void {
    this.#snackBarShow = false;
    this.messages = [];
  }

  public toggle(): void {
    this.#snackBarShow = !this.#snackBarShow;
  }

  public orderMessagesPresent(): boolean {
    return this.messages.some((msg) => msg.type === PushMessageType.order);
  }
}
